/**
 * The Studio gestation chat's starter set (`ChatStarterSet` strategy — see
 * `components/chat/chatStarters.ts`) plus the library empty-state's example
 * asks. Seeded from the mockup's `EXAMPLE_APP_ASKS` (studio spec Step 4.2 —
 * the strings are copied, not imported: the demo module is deleted in
 * Step 4.4). This thread exists to BUILD an app, so every prompt is
 * build-shaped — it names the app and its parts so the agent reaches for
 * `studio/<app-dir>/` (TOOLS.md § Studio intent map), never a chat answer.
 *
 * Clicking a starter prefills the composer (never auto-sends) so the user
 * can tweak — especially the [bracketed] slots — before sending.
 */

import { BarChart3, FileText, LayoutTemplate, ListTodo } from "lucide-react";

import type { ChatStarterSet } from "@/components/chat/chatStarters";

/** The library empty-state's "asks like these" chips (mockup strings). */
export const STUDIO_EXAMPLE_ASKS: readonly string[] = [
  "A landing page for my product",
  "A one-page CV site for me",
  "A kanban board for launch tasks",
  "A dashboard for my repo's activity",
];

export const STUDIO_CHAT_STARTERS: ChatStarterSet = {
  intro: "Pick an app to build — or describe your own below.",
  starters: [
    {
      id: "landing-page",
      icon: LayoutTemplate,
      tone: "bg-home-sky/25 text-home-water",
      label: "Landing page",
      hint: "A landing page for my product",
      prompt:
        "Build me a landing page for [my product] — a hero with the pitch, a short features section, and a call-to-action.",
    },
    {
      id: "cv-site",
      icon: FileText,
      tone: "bg-home-terracotta/12 text-home-terracotta",
      label: "One-page CV",
      hint: "A one-page CV site for me",
      prompt:
        "Build me a one-page CV site for me — name, a short summary, experience, and links. I'll give you my details to fill in.",
    },
    {
      id: "kanban-board",
      icon: ListTodo,
      tone: "bg-home-candy/15 text-home-candy",
      label: "Kanban board",
      hint: "A kanban board for launch tasks",
      prompt:
        "Build me a kanban board for launch tasks — To do / Doing / Done columns where I can add, edit, and drag cards.",
    },
    {
      id: "repo-dashboard",
      icon: BarChart3,
      tone: "bg-accent-green/12 text-accent-green",
      label: "Repo dashboard",
      hint: "A dashboard for my repo's activity",
      prompt:
        "Build me a dashboard for my repo's activity — recent commits, open issues, and pull requests for [owner/repo] from the public GitHub API.",
    },
  ],
};
